const OrderWaiverStatus = require('@/lib/Waivers/OrderWaiverStatus');
const WaiverDefinition = require('@/lib/Waivers/WaiverDefinition');
const WaiverForm = require('@/lib/Waivers/WaiverForm');
const { convertKeysToCamelCase } = require('@/functions/objects');
const { hydrate, hydrateArray } = require('@/lib/Hydration');

/**
 * Handles interaction with the Private Waivers API, via the DigiTickets API.
 * API documentations for the Private and Public Waivers API can be found in the repository:
 * https://bitbucket.org/digitickets/waivers/src/master/
 * (private-api-docs.html and public-api-docs.html)
 *
 * When an order is created on DT it goes into a queue. The waivers service watches that queue and then
 * generates any waivers required for the orders. Due to this there may be a delay between the order
 * being created and the waivers being created.
 */
class WaiversService {
    /**
     * @param {ApiClient} apiClient
     */
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    /**
     * Get an OrderWaiverStatus object for the given orderID
     *
     * Note that waivers are always stored against the original orderID and not that of any adjustments.
     *
     * Watch out - waivers will not be created immediately upon order creation.
     * The result will say if there are waivers that are still waiting to be generated.
     *
     * @param {number} orderID
     *
     * @return {Promise<OrderWaiverStatus>}
     */
    getOrderWaiverStatus(orderID) {
        return this.apiClient
            .request(
                'GET',
                'waivers/order_waivers_due/' + orderID,
            )
            .then((response) => {
                const data = {
                    orderID,
                    ...convertKeysToCamelCase(response.data.response),
                };

                return new OrderWaiverStatus(data);
            })
            .then(async (orderWaiverStatus) => {
                // Attach the waiver forms to each waiver loaded.
                await Promise.all(orderWaiverStatus.existingWaivers.map(async (waiver) => {
                    waiver.waiverForms = await this.getWaiverForms(waiver.ID);
                }));

                return orderWaiverStatus;
            })
            .catch((error) => {
                if (error.response?.data?.response?.error) {
                    // Overwrite the error message with the error from the waivers service.
                    error.message = error.response.data.response.error;
                }

                throw error;
            });
    }

    getWaiverForms(waiverID) {
        return this.apiClient
            .request(
                'GET',
                'waivers/waiver_forms',
                {
                    waiver_id: waiverID,
                }
            )
            .then((response) => {
                const data = response.data.response.data.map(convertKeysToCamelCase);

                return hydrateArray(data, () => new WaiverForm());
            })
            .catch((error) => {
                if (error.response?.data?.response?.error) {
                    // Overwrite the error message with the error from the waivers service.
                    error.message = error.response.data.response.error;
                }

                throw error;
            });
    }

    /**
     * @param {Waiver} waiver
     * @param {string} notes
     * @param {number} formCount
     *
     * @return {Promise<Waiver>}
     */
    markWaiverCompleted(waiver, notes, formCount) {
        return this.apiClient
            .request(
                'POST',
                'waivers/complete_waiver/' + waiver.ID,
                {
                    notes,
                    form_count: formCount,
                },
                true // JSON
            )
            .then((response) => {
                const data = convertKeysToCamelCase(response.data.response);
                return hydrate(data, waiver);
            })
            .then(async (w) => {
                // Re-attach the waiver forms to the waiver.
                waiver.waiverForms = await this.getWaiverForms(w.ID);

                return waiver;
            })
            .catch((error) => {
                if (error.response?.data?.response?.error) {
                    // Overwrite the error message with the error from the waivers service.
                    error.message = error.response.data.response.error;
                }

                throw error;
            });
    }

    /**
     *
     * @param {number} waiverFormID
     */
    removeWaiverForm(waiverFormID) {
        return this.apiClient
            .request(
                'DELETE',
                'waivers/waiver_form/' + waiverFormID
            )
            .then((response) => {
                return response.status === 200;
            })
            .catch((error) => {
                if (error.response?.data?.response?.error) {
                    // Overwrite the error message with the error from the waivers service.
                    error.message = error.response.data.response.error;
                }

                throw error;
            });
    }

    /**
     * @return {Promise<WaiverDefinition[]>}
     */
    getWaiverDefinitions() {
        return this.apiClient
            .request(
                'GET',
                'waivers/waiver_definitions',
            )
            .then((response) => {
                const data = response.data.response.data.map(convertKeysToCamelCase);
                console.log('data', data);

                return data.map((definitionData) => new WaiverDefinition(definitionData));
            });
    }

    /**
     * @param {TicketRepository} ticketRepository
     *
     * @return {Promise}
     */
    async attachWaiverStatusToTickets(ticketRepository) {
        // Load all waiver definitions.
        const waiverDefinitions = await this.getWaiverDefinitions();

        // Load all tickets.
        /**
         * @type {Ticket[]}
         */
        const tickets = await ticketRepository.findAll();

        // For each ticket we do not already know the waiver status for:
        // Check if there is a waiver with that itemID. Set the hasWaiver property as appropriate.
        // Then re-save the ticket.
        const ticketsToSave = [];
        for (let i = 0; i < tickets.length; i++) {
            if (tickets[i].hasWaiver !== null) {
                continue;
            }
            tickets[i].hasWaiver = !!waiverDefinitions.find((wd) => wd.itemIDs.includes(tickets[i].ID));
            ticketsToSave.push(tickets[i]);
        }

        await ticketRepository.persistMany(ticketsToSave);
    }
}

module.exports = WaiversService;
